import { useState } from "react";
import { Loader2, Search, Users, X } from "lucide-react";
import { trpc } from "@/lib/trpc";

/** Show the title filter once the list is long enough to need it. */
const SEARCH_THRESHOLD = 8;

/**
 * "Use a saved character" — pick the finished lead card set of an earlier
 * project so the new video keeps the same person, wardrobe and all
 * (shared/directorSavedCharacter.ts). Rendered inline like MyAssetsDialog so
 * clicks inside it never count as an outside interaction of the parent
 * Radix dialog.
 */
export type SavedCharacterSummary = {
  sourceProjectId: string;
  sourceTitle: string;
  characterImageUrl: string | null;
  base: { imageUrl: string };
  looks: Array<{ lookId: string; name: string; imageUrl: string }>;
  savedAt: string;
};

interface SavedCharactersDialogProps {
  open: boolean;
  onClose: () => void;
  onPick: (character: SavedCharacterSummary) => void;
  zIndex?: number;
}

export default function SavedCharactersDialog({
  open,
  onClose,
  onPick,
  zIndex = 130,
}: SavedCharactersDialogProps) {
  const query = trpc.director.listSavedCharacters.useQuery(undefined, {
    enabled: open,
    staleTime: 60_000,
  });
  const [search, setSearch] = useState("");
  if (!open) return null;
  const allCharacters = query.data?.characters ?? [];
  const needle = search.trim().toLowerCase();
  // Series makers keep dozens of songs; the server now returns every
  // distinct character (archimbold, Sep 22 2026), so let them find an old
  // song by title instead of scrolling.
  const characters = needle
    ? allCharacters.filter((character) =>
        character.sourceTitle.toLowerCase().includes(needle),
      )
    : allCharacters;
  const showSearch = allCharacters.length >= SEARCH_THRESHOLD;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex }}
      role="dialog"
      aria-modal="true"
      aria-label="Saved characters"
    >
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-3xl max-h-[85vh] flex flex-col rounded-2xl border border-white/10 bg-[oklch(0.14_0.02_270)] shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-3 shrink-0">
          <div>
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <Users className="h-4 w-4 text-amber-300" />
              Saved characters
            </h2>
            <p className="text-xs text-white/50 mt-0.5">
              Reuse a finished character from an earlier video — same face,
              same wardrobe, no new reference cards to generate.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {showSearch && (
          <div className="px-5 pb-3 shrink-0">
            <label className="flex items-center gap-2 h-9 px-3 rounded-lg border border-white/10 bg-white/[0.04] text-white/80 focus-within:border-white/30">
              <Search className="h-4 w-4 text-white/40 shrink-0" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${allCharacters.length} saved characters by song title`}
                aria-label="Search saved characters"
                className="flex-1 min-w-0 bg-transparent text-sm outline-none placeholder:text-white/35"
              />
            </label>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 pb-5">
          {query.isLoading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-white/60">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading your characters…
            </div>
          ) : query.error ? (
            <p className="py-16 text-center text-sm text-red-300">
              Couldn't load saved characters. Please try again.
            </p>
          ) : characters.length === 0 ? (
            <p className="py-16 text-center text-sm text-white/60">
              {needle
                ? `No saved character matches "${search.trim()}".`
                : "No saved characters yet. Finish the reference cards of a music video once and its character shows up here."}
            </p>
          ) : (
            <ul className="space-y-3">
              {characters.map((character) => {
                const date = new Date(character.savedAt);
                const dateLabel = Number.isNaN(date.getTime())
                  ? ""
                  : date.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    });
                return (
                  <li
                    key={character.sourceProjectId}
                    className="rounded-xl border border-white/10 bg-white/[0.03] p-3 flex items-center gap-3"
                  >
                    <img
                      src={character.characterImageUrl ?? character.base.imageUrl}
                      alt=""
                      className="h-16 w-16 rounded-lg object-cover shrink-0 bg-black/40"
                      loading="lazy"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-white truncate">
                        {character.sourceTitle}
                      </div>
                      <div className="text-xs text-white/50">
                        {character.looks.length} look
                        {character.looks.length === 1 ? "" : "s"}
                        {dateLabel ? ` · ${dateLabel}` : ""}
                      </div>
                      {character.looks.length > 0 && (
                        <div className="mt-2 flex gap-1.5 overflow-x-auto">
                          {character.looks.map((look) => (
                            <img
                              key={look.lookId}
                              src={look.imageUrl}
                              alt={look.name}
                              title={look.name}
                              className="h-10 w-10 rounded-md object-cover shrink-0 bg-black/40"
                              loading="lazy"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => onPick(character)}
                      className="h-9 px-4 rounded-lg bg-white text-black text-sm font-semibold hover:bg-white/90 transition shrink-0"
                    >
                      Use
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
